import { CheckSquareOutlined, CloseSquareOutlined, PlusOutlined } from "@ant-design/icons";
import type { TableColumnsType } from "antd";
import { Button, Tooltip, Typography, theme } from "antd";
import { ColumnFilterItem } from "antd/es/table/interface";
import SlideMenuGuide, { useSlideMenuGuide } from "components/atoms/SlideMenuGuide";
import DataTable from "components/molecules/DataTable";
import ProhibitedArea from "components/molecules/ProhibitedArea";
import moment from "moment";
import React from "react";
import CypressTestIds from "../../../cypress/CypressTestIds";
import SchemaModelActionButtons from "./SchemaModelActionButtons";

const { useToken } = theme;

interface ISchemaModelListProps {
    autoScrollTo?: boolean;
    permission: IPermissionState;
    schemaModelList: ISchema[];
    selectedSchemaModel?: ISchema;
    onCreateNewSchemaModel: () => void;
    onDeleteSchemaModel: (s: ISchema) => void;
    onViewSchemaModel: (id: string) => void;
    onEditSchemaModel: (id: string) => void;
    onViewSchemaModelHistory: (id: string) => void;
    onViewSchemaModelLineage: (id: string) => void;
    onPageSelect: (page: number, pageSize: number) => void;
    onSchemaModelSelected?: (s: ISchema | undefined) => void;
}

const SchemaModelList = ({
    autoScrollTo = false,
    permission,
    schemaModelList,
    selectedSchemaModel,
    onCreateNewSchemaModel,
    onDeleteSchemaModel,
    onViewSchemaModel,
    onEditSchemaModel,
    onViewSchemaModelHistory,
    onViewSchemaModelLineage,
    onPageSelect,
    onSchemaModelSelected,
}: ISchemaModelListProps) => {
    const { token } = useToken();
    const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>(selectedSchemaModel ? [selectedSchemaModel.schemaId] : []);
    const [showMenuGuide, setShowMenuGuide] = React.useState<boolean>(false);
    const { isReady, guidePostion } = useSlideMenuGuide("ant-table-header", "ant-table-row");

    React.useEffect(() => {
        setShowMenuGuide(isReady);
    }, [isReady]);

    const allNames: ColumnFilterItem[] = React.useMemo(() => {
        const states: ColumnFilterItem[] = [];
        schemaModelList.forEach((a) => {
            if (!states.some((s) => s.value === a.name)) {
                states.push({
                    text: a.name,
                    value: a.name,
                });
            }
        });
        return states;
    }, [schemaModelList]);

    const allStates: ColumnFilterItem[] = React.useMemo(() => {
        const states: ColumnFilterItem[] = [];
        schemaModelList.forEach((a) => {
            if (!states.some((s) => s.value === a.state)) {
                states.push({
                    text: a.state,
                    value: a.state,
                });
            }
        });
        return states;
    }, [schemaModelList]);

    const allUpdatedBy: ColumnFilterItem[] = React.useMemo(() => {
        const states: ColumnFilterItem[] = [];
        schemaModelList.forEach((a) => {
            if (!states.some((s) => s.value === a.updatedByEmail)) {
                states.push({
                    text: a.updatedByEmail,
                    value: a.updatedByEmail,
                });
            }
        });
        return states;
    }, [schemaModelList]);

    const onClickRow = (record: ISchema) => {
        return {
            onClick: () => {
                setSelectedRowKeys([record.schemaId]); // Supports single selection only
                onSchemaModelSelected && onSchemaModelSelected(schemaModelList.find((schema) => schema.schemaId === record.schemaId));
            },
        };
    };

    const columns: TableColumnsType<ISchema> = React.useMemo(
        () => [
            {
                fixed: "left",
                width: 40,
                className: "overflow-visible !p-1",
                render: (_v, schema) => (
                    <SchemaModelActionButtons
                        schema={schema}
                        selected={selectedRowKeys.some((a) => a === schema.schemaId)}
                        permission={permission}
                        onViewSchemaModel={onViewSchemaModel}
                        onEditSchemaModel={onEditSchemaModel}
                        onViewSchemaModelHistory={onViewSchemaModelHistory}
                        onViewSchemaModelLineage={onViewSchemaModelLineage}
                        onDeleteSchemaModel={onDeleteSchemaModel}
                    />
                ),
            },
            {
                width: 300,
                dataIndex: "name",
                title: "Schema",
                ellipsis: {
                    showTitle: false,
                },
                render: (value, _s, _i) => (
                    <Tooltip placement="topLeft" title={value}>
                        {value}
                    </Tooltip>
                ),
                sorter: (a, b) => a.name.length - b.name.length,
                filters: allNames,
                filterMode: "menu",
                filterSearch: true,
                onFilter: (value, record) => record.name.startsWith(value as string),
            },
            {
                width: 100,
                dataIndex: "process",
                title: "Process",
                ellipsis: {
                    showTitle: false,
                },
                render: (value, _s, _i) => (
                    <Tooltip placement="topLeft" title={value}>
                        {value}
                    </Tooltip>
                ),
            },
            {
                width: 80,
                dataIndex: "fieldsCount",
                title: "Fields",
                ellipsis: {
                    showTitle: false,
                },
                render: (value, _s, _i) => (
                    <Tooltip placement="topLeft" title={value}>
                        {value}
                    </Tooltip>
                ),
            },
            {
                width: 100,
                dataIndex: "dataSetCount",
                title: "DataSets",
                ellipsis: {
                    showTitle: false,
                },
                render: (value, _s, _i) => (
                    <Tooltip placement="topLeft" title={value || 0}>
                        {value || 0}
                    </Tooltip>
                ),
            },
            {
                width: 100,
                dataIndex: "state",
                title: "State",
                ellipsis: {
                    showTitle: false,
                },
                render: (value, _s, _i) => (
                    <Tooltip placement="topLeft" title={value}>
                        {value}
                    </Tooltip>
                ),
                filters: allStates,
                filterMode: "menu",
                filterSearch: true,
                onFilter: (value, record) => record.state.startsWith(value as string),
            },
            {
                width: 80,
                dataIndex: "sharing",
                title: "Sharing",
                ellipsis: {
                    showTitle: false,
                },
                className: "",
                render: (value, _s, _i) => {
                    return value ? (
                        <CheckSquareOutlined style={{ fontSize: "16px", color: token.colorSuccessText }} />
                    ) : (
                        <CloseSquareOutlined style={{ fontSize: "16px", color: token.colorTextDisabled }} />
                    );
                },
            },
            {
                width: 220,
                dataIndex: "updatedDate",
                title: "Last Updated",
                ellipsis: {
                    showTitle: false,
                },
                render: (value, _s, _i) => {
                    let dateValue = moment.utc(value).toDate().toLocaleString();
                    return (
                        <Tooltip placement="topLeft" title={dateValue}>
                            {dateValue}
                        </Tooltip>
                    );
                },
            },
            {
                width: 300,
                dataIndex: "updatedByEmail",
                title: "Updated By",
                ellipsis: {
                    showTitle: false,
                },
                render: (value, _s, _i) => (
                    <Tooltip placement="topLeft" title={value}>
                        {value}
                    </Tooltip>
                ),
                filters: allUpdatedBy,
                filterMode: "menu",
                filterSearch: true,
                onFilter: (value, record) => record.updatedByEmail.startsWith(value as string),
            },
        ],
        [
            allNames,
            allStates,
            allUpdatedBy,
            onDeleteSchemaModel,
            onEditSchemaModel,
            onViewSchemaModel,
            onViewSchemaModelHistory,
            onViewSchemaModelLineage,
            permission,
            selectedRowKeys,
            token.colorSuccessText,
            token.colorTextDisabled,
        ],
    );

    if (!permission) {
        return <ProhibitedArea></ProhibitedArea>;
    }

    return (
        <DataTable<ISchema>
            dataTestId={CypressTestIds.SCHEMA_LIST_TABLE}
            header={
                <>
                    <Typography.Title level={3} className="!mb-0">
                        Schema
                    </Typography.Title>
                    <Button disabled={!permission.canAdd} type="primary" onClick={onCreateNewSchemaModel} data-testid={CypressTestIds.CREATE_NEW_SCHEMA_BUTTON}>
                        <PlusOutlined /> Create a new schema
                    </Button>
                </>
            }
            resizable
            hideSelectionColumn
            autoScrollTo={autoScrollTo}
            tableProps={{
                dataSource: schemaModelList,
                columns: columns,
                onRow: onClickRow,
                onChange: (pagination, _filters, _sorter, extra) =>
                    extra.action === "paginate" && onPageSelect && onPageSelect(pagination.current ?? 1, pagination.pageSize ?? 100),
                rowSelection: {
                    type: "radio",
                    selectedRowKeys,
                    onChange: (selected) => setSelectedRowKeys(selected),
                },
                rowKey: "schemaId",
                rowClassName: "cursor-pointer",
                pagination: {
                    defaultPageSize: 50,
                    pageSize: 50,
                },
            }}
        >
            {schemaModelList.length > 0 && <SlideMenuGuide open={showMenuGuide} position={guidePostion} onClose={() => setShowMenuGuide(false)} />}
        </DataTable>
    );
};

export default SchemaModelList;
